import React, { useState } from 'react';
import { webOrigin } from '../utils/brand';
import { shareLink, isNative } from '../utils/nativeShare';
import ShareDialog from './ShareDialog';

const shareIcon = (size) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7" />
    <polyline points="16 6 12 2 8 6" />
    <line x1="12" y1="2" x2="12" y2="15" />
  </svg>
);

// Touch devices get the native share sheet (great for Messages/WhatsApp/IG in one
// tap); desktop browsers get our own dialog, since the OS sheet is clunky-to-broken
// inside a desktop browser.
function isTouchDevice() {
  if (typeof navigator === 'undefined') return false;
  if (navigator.maxTouchPoints > 0) return true;
  return typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
}

/**
 * Share affordance for a card. Phones open the native share sheet; desktop opens a
 * ShareDialog (copy link + socials). A custom `onClick` (e.g. the signal share
 * modal) overrides both. Sits inside clickable cards, so it stops propagation.
 *
 * Props: url (required), title, text, label (show the word "Share"), className,
 *        stretch (square that matches row height), sizeClass (explicit sizing).
 */
export default function ShareButton({ url = '', title = 'Portal', text = '', label = false, className = '', onClick = null, stretch = false, sizeClass = '', style = undefined, bare = false, iconSize = 14 }) {
  const [dialogOpen, setDialogOpen] = useState(false);

  const absolute = /^https?:\/\//.test(url) ? url : `${webOrigin()}${url.startsWith('/') ? '' : '/'}${url}`;

  const onShare = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    // A custom handler (e.g. open a rich share modal) takes over entirely.
    if (onClick) { onClick(); return; }
    // Natively this must go through the OS sheet: on Android navigator.share
    // doesn't exist, and on iOS it exists and does nothing, which made the same
    // bug look like two different ones. shareLink returns false when there's no
    // sheet to present, and only then does the in-app dialog make sense.
    if (isNative() || isTouchDevice()) {
      try {
        if (await shareLink({ title, text, url: absolute })) return;
      } catch { /* fall through to the dialog */ }
    }
    setDialogOpen(true);
  };

  const size = sizeClass || (label ? 'px-3 py-2.5' : (stretch ? 'aspect-square self-stretch' : 'h-10 w-10'));
  // `bare` = the caller's container already draws the 2px frame (the action
  // strip on an event card/row divides its segments with border-left).
  const frame = bare ? '' : 'border-2 border-black bg-white';

  return (
    <>
      <button
        type="button"
        onClick={onShare}
        aria-label="Share"
        title="Share"
        style={style}
        className={`inline-flex flex-shrink-0 items-center justify-center gap-1.5 ${frame} font-mono text-[10px] font-bold uppercase tracking-wide text-black transition-colors hover:bg-black hover:text-white ${size} ${className}`}
      >
        {shareIcon(iconSize)}
        {label && <span>Share</span>}
      </button>
      <ShareDialog isOpen={dialogOpen} onClose={() => setDialogOpen(false)} url={absolute} title={title} text={text} />
    </>
  );
}
